/* eslint-disable no-unused-vars */
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as S from './AppInstallPostbackForm.styled';
import { useForm } from 'react-hook-form';
import { Buttons } from 'components';
import Section from './components/Section';
import { routes } from 'config';
import { manageAppsAPI } from 'services/manageApps.service';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  postBackSchema,
  installPostBackFields,
  eventPostBackFields,
  rejectedInstallPostbackFields,
} from './validation';
import { useFacade } from './useFacade';
import { showPolipop } from 'utils';

let timer_id;

const createDataToSave = ({
  sectionName,
  createPreview,
  data,
  pba_id,
  pba_name,
  platform_name,
}) => {
  const newData = {
    postBackUrl: createPreview(data[sectionName]),
    appId: pba_id,
    pbaName: pba_name,
    pUrl: data[sectionName].baseUrl ?? '',
    platform: platform_name,
    isActivate: data[sectionName].state ? 1 : 0,
    isSendMail: data[sectionName].state ? 1 : 0,
  };
  return newData;
};

const AppInstallPostbackForm = (props) => {
  const {
    pba_name,
    pba_id,
    platform_name,
    postBackUrl,
    postBackUrlActive,
    eventPostBackUrl,
    eventPostBackUrlActive,
    rejectedInstallsPostBackUrl,
    rejectedInstallsPostBackUrlActive,
  } = props;
  const [saveAppInstallPostBackUrl, saveAppInstallPostbackResult] =
    manageAppsAPI.useSaveAppInstallPostbackMutation();
  const [saveAppEventPostBackUrl, saveAppEventPostbackResult] =
    manageAppsAPI.useSaveAppEventPostbackMutation();
  const [saveAppRejectedInstallPostBackUrl, saveAppRejectedInstallPostbackResult] =
    manageAppsAPI.useSaveAppRejectedInstallPostbackMutation();

  const [createUrlObj, createPreview, createPreview2] = useFacade();

  const installPostBackUrlData = useMemo(() => {
    return {
      ...createUrlObj(postBackUrl, installPostBackFields),
      state: Boolean(postBackUrlActive),
    };
  }, [postBackUrl, postBackUrlActive]);

  const eventPostBackUrlData = useMemo(() => {
    return {
      ...createUrlObj(eventPostBackUrl, eventPostBackFields),
      state: Boolean(eventPostBackUrlActive),
    };
  }, [eventPostBackUrl, eventPostBackUrlActive]);

  const rejectedInstallsPostBackUrlData = useMemo(() => {
    return {
      ...createUrlObj(rejectedInstallsPostBackUrl, rejectedInstallPostbackFields),
      state: Boolean(rejectedInstallsPostBackUrlActive),
    };
  }, [rejectedInstallsPostBackUrl, rejectedInstallsPostBackUrlActive]);

  const {
    handleSubmit,
    control,
    watch,
    reset,
    getValues,
    trigger,
    formState: { isDirty, errors },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      installPostBack: installPostBackUrlData,
      eventPostBack: eventPostBackUrlData,
      rejectedInstallPostBack: rejectedInstallsPostBackUrlData,
    },
    resolver: yupResolver(postBackSchema),
  });

  useEffect(() => {
    trigger([
      'installPostBack.baseUrl',
      'eventPostBack.baseUrl',
      'rejectedInstallPostBack.baseUrl',
    ]);
  }, [trigger]);

  const [redirect, setRedirect] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const onSubmit = async (data) => {
    // const newData = {
    //   postBackUrl: createPreview(data.installPostBack),
    //   appId: pba_id,
    //   pbaName: pba_name,
    //   pUrl: data.installPostBack.baseUrl,
    //   platform: platform_name,
    //   isActivate: data.installPostBack.state ? 1 : 0,
    //   isSendMail: data.installPostBack.state ? 1 : 0,
    // };
    setIsSubmitting(true);
    const redirectFunction = redirect ? goToManageApps : () => {};
    const installData = createDataToSave({
      sectionName: 'installPostBack',
      createPreview: createPreview2,
      data,
      pba_id,
      pba_name,
      platform_name,
    });

    const eventData = createDataToSave({
      sectionName: 'eventPostBack',
      createPreview: createPreview2,
      data,
      pba_id,
      pba_name,
      platform_name,
    });
    const rejectedInstallData = createDataToSave({
      sectionName: 'rejectedInstallPostBack',
      createPreview: createPreview2,
      data,
      pba_id,
      pba_name,
      platform_name,
    });
    try {
      const results = await Promise.allSettled([
        saveAppEventPostBackUrl(eventData),
        saveAppRejectedInstallPostBackUrl(rejectedInstallData),
        saveAppInstallPostBackUrl(installData),
      ]);
      const successMessages = results
        .filter((result) => result.status === 'fulfilled' && result.value?.data?.success)
        .map((result) => result.value?.data?.message);

      const errorMessages = results
        .filter((result) => result.status === 'fulfilled' && result.value?.error)
        .flatMap((result) => {
          const errors = result.value?.error?.data?.errors ?? {};
          return Object.entries(errors).map(
            ([field, messages]) => `${field}: ${messages.join(', ')}`,
          );
        });
      if (successMessages.length === 3) {
        showPolipop({ isSuccess: true }, () => {
          reset(watch());
          redirectFunction();
        });
      }

      if (errorMessages.length > 0) {
        showPolipop({ error: { data: errorMessages[0] } });
      }
    } catch (error) {
      console.log('error', error);
    }
    // const eventRes = await saveAppEventPostBackUrl(eventData);
    // const rejectedInstallRes = await saveAppEventPostBackUrl(rejectedInstallData);
    // const installRes = await saveAppInstallPostBackUrl(installData);
    //const installRes = await saveAppInstallPostBackUrl(newData);
    setIsSubmitting(false);
  };

  const goToManageApps = useCallback(() => {
    const link = routes.list.find((route) => route.id === 'manageApps').url;
    navigate(link);
  }, []);

  const handleSaveAndClose = () => {
    handleSubmit();
    setRedirect(true);
  };

  return (
    <S.Container>
      <S.Form onSubmit={handleSubmit(onSubmit)} id='appInstall'>
        <S.Box>
          <Section
            getValues={getValues}
            fieldList={installPostBackFields}
            title='Install Postback'
            sectionName='installPostBack'
            control={control}
            createPreview={createPreview2}
            errors={errors}
          >
            Set-up a S2S callbacks from Appnext, notifying you of a conversion (install) made
            through your app.
            <br /> In order to receive postbacks, you should create a callback URL on your server,
            to-which Appnext will pass URL parameters.
            <br /> Next: you should add your base URL, and place-holders for the postback parameters
            you wish to receive.
          </Section>
        </S.Box>
        <S.Box>
          <Section
            getValues={getValues}
            fieldList={eventPostBackFields}
            title='Event Postback'
            sectionName='eventPostBack'
            control={control}
            createPreview={createPreview2}
            errors={errors}
          >
            Set-up a S2S callbacks from Appnext, notifying you of a conversion (event) made through
            your app.
            <br /> In order to receive postbacks, you should create a callback URL on your server,
            to-which Appnext will pass URL parameters.
            <br /> Next: you should add your base URL, and place-holders for the postback parameters
            you wish to receive.
          </Section>
        </S.Box>
        <S.Box>
          <Section
            getValues={getValues}
            fieldList={rejectedInstallPostbackFields}
            title='Rejected Install Postback'
            sectionName='rejectedInstallPostBack'
            control={control}
            createPreview={createPreview2}
            errors={errors}
          >
            Set-up a S2S callbacks from Appnext, notifying you of a rejected install made through
            your app.
            <br /> In order to receive postbacks, you should create a callback URL on your server,
            to-which Appnext will pass URL parameters.
            <br />
            Next: you should add your base URL, and place-holders for the postback parameters you
            wish to receive.
          </Section>
        </S.Box>
        <S.BtnGroup>
          <Buttons.PrimaryNormal
            type='submit'
            isDisabled={!isDirty || isSubmitting}
            id='saveButton'
            name='Add New App'
            onClick={handleSubmit}
          >
            Save Settings
          </Buttons.PrimaryNormal>
          <Buttons.SecondaryNormal
            type='submit'
            isDisabled={!isDirty || isSubmitting}
            id='saveAndCloseButton'
            name='Add New App'
            onClick={handleSaveAndClose}
          >
            Save & Close
          </Buttons.SecondaryNormal>
          <Buttons.SecondaryNormal id='cancelButton' onClick={goToManageApps}>
            Cancel
          </Buttons.SecondaryNormal>
        </S.BtnGroup>
      </S.Form>
    </S.Container>
  );
};

export default AppInstallPostbackForm;
